import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	Barrel,
	BarrelAttachment,
	BarrelLayout,
	PassId,
	RollMaterial,
} from "../models/Barrel";

// ============================================
// BARRELS
// ============================================

export interface GetBarrelsParams {
	id?: number | null;
	search?: string | null;
	standId?: number | null;
	onlyActives?: boolean;
	includeLayout?: boolean;
}

export interface GetBarrelsResult {
	barrels: Barrel[];
	layouts: BarrelLayout[];
}

export const getBarrels = async (
	params: GetBarrelsParams = {},
): Promise<GetBarrelsResult> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.id ?? null },
		{ name: "Search", value: params.search ?? null },
		{ name: "StandId", value: params.standId ?? null },
		{ name: "OnlyActives", value: params.onlyActives ?? false },
		{ name: "IncludeLayout", value: params.includeLayout ?? false },
	];

	const resp = await apiService.callV2("[ROLLS].[GetBarrels]", parameters);

	if (resp.ok) {
		const barrels = get(resp, "data.tables[0].rows", []) as Barrel[];
		const layouts = params.includeLayout
			? (get(resp, "data.tables[1].rows", []) as BarrelLayout[])
			: [];
		return { barrels, layouts };
	}
	throw new Error(resp.message || "Error fetching barrels");
};

export const upsertBarrel = async (barrel: Barrel): Promise<Barrel> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: barrel.Id },
		{ name: "Name", value: barrel.Name },
		{ name: "DwgFileName", value: barrel.DwgFileName },
		{ name: "StandId", value: barrel.StandId },
		{ name: "BottomDiameter", value: barrel.BottomDiameter },
		{ name: "BottomDiameterScrap", value: barrel.BottomDiameterScrap },
		{ name: "BottomRollMaterialId", value: barrel.BottomRollMaterialId },
		{ name: "TopDiameter", value: barrel.TopDiameter },
		{ name: "TopDiameterScrap", value: barrel.TopDiameterScrap },
		{ name: "TopRollMaterialId", value: barrel.TopRollMaterialId },
		{ name: "Comments", value: barrel.Comments },
		{ name: "PitProgramName", value: barrel.PitProgramName },
		{ name: "Active", value: barrel.Active },
	];

	const resp = await apiService.callV2("[ROLLS].[UpsertBarrel]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as Barrel;
	}
	throw new Error(resp.message || "Error saving barrel");
};

export const deleteBarrel = async (barrelId: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: barrelId }];

	const resp = await apiService.callV2("[ROLLS].[RemoveBarrel]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting barrel");
	}
};

// ============================================
// ROLL MATERIALS (for dropdown)
// ============================================

export const getRollMaterials = async (): Promise<RollMaterial[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "OnlyActives", value: true }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetRollMaterials]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as RollMaterial[];
	}
	throw new Error(resp.message || "Error fetching roll materials");
};

// ============================================
// PASS IDS (for barrel layouts dropdown)
// ============================================

export interface GetPassIdsParams {
	standId?: number | null;
	passTypeId?: number | null;
	passShapeId?: number | null;
	onlyActives?: boolean;
}

export const getPassIds = async (
	params: GetPassIdsParams = {},
): Promise<PassId[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "StandId", value: params.standId ?? null },
		{ name: "PassTypeId", value: params.passTypeId ?? null },
		{ name: "PassShapeId", value: params.passShapeId ?? null },
		{ name: "OnlyActives", value: params.onlyActives ?? true },
	];

	const resp = await apiService.callV2("[MILL].[GetPassIds]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as PassId[];
	}
	throw new Error(resp.message || "Error fetching pass ids");
};

// ============================================
// BARREL LAYOUTS
// ============================================

export interface UpsertBarrelLayoutParams {
	id?: number | null;
	barrelId: number;
	passId: number;
	position?: number | null;
}

export const upsertBarrelLayout = async (
	params: UpsertBarrelLayoutParams,
): Promise<BarrelLayout> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.id ?? null },
		{ name: "BarrelId", value: params.barrelId },
		{ name: "PassId", value: params.passId },
		{ name: "Position", value: params.position ?? null },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertBarrelLayout]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as BarrelLayout;
	}
	throw new Error(resp.message || "Error saving barrel layout");
};

export const deleteBarrelLayout = async (layoutId: number): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Id", value: layoutId }];

	const resp = await apiService.callV2(
		"[ROLLS].[RemoveBarrelLayout]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting barrel layout");
	}
};

export interface ReorderBarrelLayoutParams {
	id: number;
	newPosition: number;
}

export const reorderBarrelLayout = async (
	params: ReorderBarrelLayoutParams,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.id },
		{ name: "NewPosition", value: params.newPosition },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[ReorderBarrelLayout]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error reordering barrel layout");
	}
};

// ============================================
// BARREL ATTACHMENTS
// ============================================

export const getBarrelAttachments = async (
	barrelId: number,
): Promise<BarrelAttachment[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "BarrelId", value: barrelId }];

	const resp = await apiService.callV2(
		"[ROLLS].[GetBarrelAttachments]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as BarrelAttachment[];
	}
	throw new Error(resp.message || "Error fetching barrel attachments");
};

export interface UpsertBarrelAttachmentParams {
	barrelId: number;
	uploadFileId: number;
	attachmentName: string;
	attachmentURL: string;
}

export const upsertBarrelAttachment = async (
	params: UpsertBarrelAttachmentParams,
): Promise<BarrelAttachment> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "BarrelId", value: params.barrelId },
		{ name: "UploadFileId", value: params.uploadFileId },
		{ name: "AttachmentName", value: params.attachmentName },
		{ name: "AttachmentURL", value: params.attachmentURL },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertBarrelAttachment]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as BarrelAttachment;
	}
	throw new Error(resp.message || "Error saving barrel attachment");
};

export const deleteBarrelAttachment = async (
	barrelAttachmentId: number,
): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "BarrelAttachmentId", value: barrelAttachmentId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[DeleteBarrelAttachment]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting barrel attachment");
	}
};
